import {
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import DataGridSSR from "../../moleculas/datagrids";
import {
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ActionsWrapper from "../../atomos/ActionWrapper";
import { Template } from "../../../store/schemas/template.schema";
import { useAppDispatch, useAppSelector } from "../../../store/hook/store.hook";
import { changePaginated } from "../../../store/slices/templates.slice";
import { useDeleteTemplate } from "../../../hooks/api/useDeleteTemplate";
import { useConfirm } from "material-ui-confirm";
import { useState } from "react";
import TemplateEditForm from "../plantillas/forms/TemplateEditForm";
import { useLoaderTemplates } from "../../../hooks/templates/useLoaderTemplates";
import { useRoleCheck } from "../../../hooks/useRoleCheck";
import dayjs from "dayjs";



const DataGridTemplates = () => {
  const canDelete = useRoleCheck(["superadmin"]);
  const { loadTemplates } = useLoaderTemplates();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
    null
  );
  const dispatch = useAppDispatch();
  const confirm = useConfirm();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info" | "warning", 
  });
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const { templates, meta, paginated, loading } = useAppSelector(
    (state) => state.templates
  );
  const { mutate: deleteTemplate, isPending } = useDeleteTemplate();

  const handleEditSuccess = () => {
    loadTemplates();
  };
  const handleEditClick = (template: Template) => {
    setSelectedTemplate(template);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedTemplate(null);
  };

  const handleDelete = (id: number) => {
    setDeletingId(id);

    confirm({
      title: "Confirmar eliminación",
      description: "¿Estás seguro de que deseas eliminar esta plantilla?",
      confirmationText: "Eliminar",
      cancellationText: "Cancelar",
      confirmationButtonProps: { color: "error", variant: "contained" },
      cancellationButtonProps: { variant: "outlined" },
    })
      .then((result) => {
        if (result.confirmed && result.reason === "confirm") {
          return new Promise<void>((resolve) => {
            deleteTemplate(id, {
              onSuccess: () => {
                setSnackbar({
                  open: true,
                  message: "Plantilla eliminada correctamente",
                  severity: "success",
                });
                loadTemplates();
                resolve();
              },
              onError: () => {
                setSnackbar({
                  open: true,
                  message: "Error al eliminar la plantilla",
                  severity: "error",
                });
                resolve();
              },
            });
          });
        }
        else if (!result.confirmed && result.reason === "cancel") {
          setSnackbar({
            open: true,
            message: "Eliminación cancelada por el usuario",
            severity: "info", 
          });
        }
        else if (!result.confirmed && result.reason === "natural") {
          //nada
        }
      })
      .catch((error) => {
        console.error("Error inesperado:", error);
        setSnackbar({
          open: true,
          message: "Error inesperado al procesar la solicitud",
          severity: "error",
        });
      })
      .finally(() => {
        setDeletingId(null);
      });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const columns: GridColDef<Template>[] = [
    {
      field: "id",
      headerName: "ID",
    },
    {
      field: "Nombre",
      headerName: "Nombre",
      flex: 1,
      renderCell: (params: GridRenderCellParams<Template>) => params.row.name,
    },
    {
      field: "created_at",
      headerName: "Fecha Creación",
      flex: 0.5,
      renderCell: (params: GridRenderCellParams<Template>) =>
        params.row.created_at
          ? dayjs(params.row.created_at).format("DD/MM/YYYY")
          : "",
    },
    {
      field: "actions",
      headerName: "Acciones",
      headerAlign: "center",
      renderHeader: () => "",
      flex: 0.5,
      renderCell: (params: GridRenderCellParams<Template>) => (
        <ActionsWrapper>
          <Tooltip title="Editar">
            <IconButton
              onClick={() => handleEditClick(params.row)}
              disabled={isPending && deletingId === params.row.id}
            >
              <EditNoteOutlinedIcon />
            </IconButton>
          </Tooltip>
          {canDelete && (
            <Tooltip title="Eliminar">
              <IconButton
                onClick={() => handleDelete(params.row.id)}
                color="error"
                disabled={isPending && deletingId === params.row.id}
              >
                {isPending && deletingId === params.row.id ? (
                  <CircularProgress size={24} />
                ) : (
                  <DeleteOutlineOutlinedIcon />
                )}
              </IconButton>
            </Tooltip>
          )}
        </ActionsWrapper>
      ),
    },
  ];

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    dispatch(
      changePaginated({
        page: model.page,
        limit: model.pageSize,
      })
    );
  };

  return (
    <div role="main">
      <DataGridSSR
        rows={templates || []}
        columns={columns}
        rowCount={meta?.totalItems || 0}
        paginationModel={{
          page: paginated?.page || 0,
          pageSize: paginated?.limit || 10,
        }}
        onPaginationModelChange={handlePaginationModelChange}
        loading={loading}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <TemplateEditForm
        open={editModalOpen}
        onClose={handleCloseEditModal}
        template={selectedTemplate}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};

export default DataGridTemplates;
